'use client';

/**
 * RuleBuilder Component
 *
 * Visual builder for pricing rules with support for all calculation types.
 * Includes conditions builder with AND/OR logic.
 * Uses glassmorphism styling with GlassCard sections.
 */

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Box, { type BoxProps } from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { alpha, styled, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { motion } from 'framer-motion';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { getNeonStyles, type NeonAccent } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { motionPresets, transitionPresets } from '../../theme/transitions';
import type { CalculationType, PricingCondition, PricingRule, PricingTier, TierType } from '../../types/pricingEngine';
import { GlassCard } from '../GlassCard';
import { GlassSwitch } from '../GlassSwitch';

// ============================================================================
// Types
// ============================================================================

export interface RuleBuilderProps extends Omit<BoxProps, 'onChange' | 'children'> {
  /** Existing rule to edit */
  rule?: PricingRule;
  /** Change handler - called on every change */
  onChange: (rule: PricingRule) => void;
  /** Available fields for conditions */
  availableFields?: Array<{ name: string; label: string; type: string }>;
  /** Whether the builder is disabled */
  disabled?: boolean;
  /** Neon accent color */
  neonAccent?: NeonAccent;
  /** Display variant */
  variant?: 'compact' | 'full';
  /** Additional class name */
  className?: string;
}

type ConditionOperator = PricingCondition['operator'];

// ============================================================================
// Constants
// ============================================================================

const CALCULATION_TYPES: {
  value: CalculationType;
  label: string;
  description: string;
}[] = [
  {
    value: 'FLAT',
    label: 'Flat Amount',
    description: 'Fixed amount regardless of input',
  },
  {
    value: 'PERCENTAGE',
    label: 'Percentage',
    description: 'Percentage of the base amount',
  },
  {
    value: 'TIERED',
    label: 'Tiered',
    description: 'Different rates based on value ranges',
  },
  {
    value: 'FORMULA',
    label: 'Formula',
    description: 'Custom formula-based calculation',
  },
  {
    value: 'LOOKUP',
    label: 'Lookup',
    description: 'Value from a lookup table',
  },
  {
    value: 'MATRIX',
    label: 'Matrix',
    description: 'Multi-dimensional rate matrix',
  },
];

const CONDITION_OPERATORS: {
  value: ConditionOperator;
  label: string;
  requiresArray?: boolean;
}[] = [
  { value: 'equals', label: 'Equals' },
  { value: 'notEquals', label: 'Not Equals' },
  { value: 'greaterThan', label: 'Greater Than' },
  { value: 'lessThan', label: 'Less Than' },
  { value: 'greaterThanOrEqual', label: 'Greater Than or Equal' },
  { value: 'lessThanOrEqual', label: 'Less Than or Equal' },
  { value: 'in', label: 'In List', requiresArray: true },
  { value: 'notIn', label: 'Not In List', requiresArray: true },
  { value: 'between', label: 'Between', requiresArray: true },
  { value: 'contains', label: 'Contains' },
];

const ROUNDING_MODES = [
  { value: 'none', label: 'No Rounding' },
  { value: 'up', label: 'Round Up' },
  { value: 'down', label: 'Round Down' },
  { value: 'nearest', label: 'Round to Nearest' },
];

// ============================================================================
// Styled Components
// ============================================================================

interface StyledContainerProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}

const StyledContainer = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledContainerProps>(({ theme, $isDark, $neonAccent }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  if (neonStyles) {
    return {
      borderRadius: 16,
      padding: theme.spacing(2),
      background: neonStyles.background,
      border: `1px solid ${neonStyles.border}`,
      transition: transitionPresets.card,
    };
  }

  return {
    borderRadius: 16,
    padding: theme.spacing(2),
    background: glass.background,
    backdropFilter: glass.backdropBlur,
    border: `1px solid ${glass.border}`,
    boxShadow: glass.shadow,
    transition: transitionPresets.card,
  };
});

const SectionHeader = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(2),
}));

const ConditionRow = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ theme, $isDark }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  borderRadius: 8,
  marginBottom: theme.spacing(1),
  background: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
  border: `1px solid ${$isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
  transition: transitionPresets.allFast,

  '&:hover': {
    background: $isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
  },

  '&:last-child': {
    marginBottom: 0,
  },
}));

const TierRow = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ theme, $isDark }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 100px 100px auto',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  borderRadius: 8,
  marginBottom: theme.spacing(1),
  background: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
  border: `1px solid ${$isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
  transition: transitionPresets.allFast,

  '&:hover': {
    background: $isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
  },

  '&:last-child': {
    marginBottom: 0,
  },
}));

const ConnectorChip = styled(Chip, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}>(({ theme, $isDark, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    height: 24,
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
    background: neonStyles ? alpha(neonStyles.base, 0.15) : alpha(theme.palette.primary.main, 0.1),
    color: neonStyles?.base || theme.palette.primary.main,
    border: `1px solid ${neonStyles?.border || alpha(theme.palette.primary.main, 0.3)}`,
    transition: transitionPresets.allFast,

    '&:hover': {
      background: neonStyles ? alpha(neonStyles.base, 0.25) : alpha(theme.palette.primary.main, 0.2),
    },
  };
});

const CalculationTypeCard = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $isDark: boolean;
  $isSelected: boolean;
  $neonAccent?: NeonAccent;
}>(({ theme, $isDark, $isSelected, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    padding: theme.spacing(2),
    borderRadius: 8,
    cursor: 'pointer',
    transition: transitionPresets.allFast,
    background: $isSelected
      ? neonStyles
        ? alpha(neonStyles.base, 0.15)
        : alpha(theme.palette.primary.main, 0.1)
      : $isDark
        ? 'rgba(255, 255, 255, 0.03)'
        : 'rgba(0, 0, 0, 0.02)',
    border: $isSelected
      ? `2px solid ${neonStyles?.base || theme.palette.primary.main}`
      : `1px solid ${$isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,

    '&:hover': {
      background: $isSelected
        ? neonStyles
          ? alpha(neonStyles.base, 0.2)
          : alpha(theme.palette.primary.main, 0.15)
        : $isDark
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(0, 0, 0, 0.04)',
      borderColor: $isSelected
        ? neonStyles?.base || theme.palette.primary.main
        : $isDark
          ? 'rgba(255, 255, 255, 0.15)'
          : 'rgba(0, 0, 0, 0.1)',
    },
  };
});

// ============================================================================
// Helper Functions
// ============================================================================

const createEmptyRule = (): PricingRule => ({
  id: `rule-${Date.now()}`,
  name: '',
  calculationType: 'FLAT',
  flatAmount: 0,
  isActive: true,
  priority: 1,
  currency: 'USD',
  conditions: [],
});

const createEmptyCondition = (): PricingCondition => ({
  id: `cond-${Date.now()}`,
  field: '',
  operator: 'equals',
  value: '',
  connector: 'AND',
});

const createEmptyTier = (): PricingTier => ({
  id: `tier-${Date.now()}`,
  from: 0,
  to: null,
  rate: 0,
  rateType: 'flat',
});

// ============================================================================
// Component
// ============================================================================

export const RuleBuilder: React.FC<RuleBuilderProps> = ({
  rule: initialRule,
  onChange,
  availableFields = [],
  disabled = false,
  neonAccent,
  variant = 'full',
  className,
  ...boxProps
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Internal state for the rule
  const [rule, setRule] = useState<PricingRule>(() => initialRule || createEmptyRule());

  // Update rule and notify parent
  const updateRule = useCallback(
    (updates: Partial<PricingRule>) => {
      const newRule = { ...rule, ...updates };
      setRule(newRule);
      onChange(newRule);
    },
    [rule, onChange],
  );

  // Condition handlers
  const addCondition = useCallback(() => {
    const newConditions = [...(rule.conditions || []), createEmptyCondition()];
    updateRule({ conditions: newConditions });
  }, [rule.conditions, updateRule]);

  const updateCondition = useCallback(
    (index: number, updates: Partial<PricingCondition>) => {
      const newConditions = [...(rule.conditions || [])];
      newConditions[index] = { ...newConditions[index], ...updates };
      updateRule({ conditions: newConditions });
    },
    [rule.conditions, updateRule],
  );

  const removeCondition = useCallback(
    (index: number) => {
      const newConditions = (rule.conditions || []).filter((_, i) => i !== index);
      updateRule({ conditions: newConditions });
    },
    [rule.conditions, updateRule],
  );

  const toggleConnector = useCallback(
    (index: number) => {
      const conditions = rule.conditions || [];
      if (index >= conditions.length) return;
      const currentConnector = conditions[index].connector;
      updateCondition(index, {
        connector: currentConnector === 'AND' ? 'OR' : 'AND',
      });
    },
    [rule.conditions, updateCondition],
  );

  // Tier handlers
  const addTier = useCallback(() => {
    const newTiers = [...(rule.tiers || []), createEmptyTier()];
    updateRule({ tiers: newTiers });
  }, [rule.tiers, updateRule]);

  const updateTier = useCallback(
    (index: number, updates: Partial<PricingTier>) => {
      const newTiers = [...(rule.tiers || [])];
      newTiers[index] = { ...newTiers[index], ...updates };
      updateRule({ tiers: newTiers });
    },
    [rule.tiers, updateRule],
  );

  const removeTier = useCallback(
    (index: number) => {
      const newTiers = (rule.tiers || []).filter((_, i) => i !== index);
      updateRule({ tiers: newTiers });
    },
    [rule.tiers, updateRule],
  );

  // Get neon styles
  const neonStyles = useMemo(() => (neonAccent ? getNeonStyles(neonAccent, isDark) : null), [neonAccent, isDark]);

  // Render calculation type specific fields
  const renderCalculationFields = () => {
    switch (rule.calculationType) {
      case 'FLAT':
        return (
          <TextField
            label="Flat Amount"
            type="number"
            value={rule.flatAmount || 0}
            onChange={(e) => updateRule({ flatAmount: parseFloat(e.target.value) || 0 })}
            disabled={disabled}
            fullWidth
            size="small"
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              },
            }}
          />
        );

      case 'PERCENTAGE':
        return (
          <TextField
            label="Percentage"
            type="number"
            value={rule.percentage || 0}
            onChange={(e) => updateRule({ percentage: parseFloat(e.target.value) || 0 })}
            disabled={disabled}
            fullWidth
            size="small"
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              },
            }}
          />
        );

      case 'TIERED':
        return (
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Tier Type</InputLabel>
                <Select
                  value={rule.tierType || 'GRADUATED'}
                  label="Tier Type"
                  onChange={(e) => updateRule({ tierType: e.target.value as TierType })}
                  disabled={disabled}
                >
                  <MenuItem value="GRADUATED">Graduated</MenuItem>
                  <MenuItem value="MARGINAL">Marginal</MenuItem>
                </Select>
              </FormControl>
              <IconButton
                onClick={addTier}
                disabled={disabled}
                size="small"
                sx={{
                  color: neonStyles?.base || 'primary.main',
                  '&:hover': {
                    background: alpha(neonStyles?.base || theme.palette.primary.main, 0.1),
                  },
                }}
              >
                <AddIcon />
              </IconButton>
            </Box>

            {(rule.tiers || []).length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                  From | To | Rate | Type | Actions
                </Typography>
                {(rule.tiers || []).map((tier, index) => (
                  <TierRow
                    key={tier.id}
                    $isDark={isDark}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <TextField
                      type="number"
                      value={tier.from}
                      onChange={(e) =>
                        updateTier(index, {
                          from: parseFloat(e.target.value) || 0,
                        })
                      }
                      disabled={disabled}
                      size="small"
                      placeholder="From"
                    />
                    <TextField
                      type="number"
                      value={tier.to ?? ''}
                      onChange={(e) =>
                        updateTier(index, {
                          to: e.target.value === '' ? null : parseFloat(e.target.value),
                        })
                      }
                      disabled={disabled}
                      size="small"
                      placeholder="To (empty = unlimited)"
                    />
                    <TextField
                      type="number"
                      value={tier.rate}
                      onChange={(e) =>
                        updateTier(index, {
                          rate: parseFloat(e.target.value) || 0,
                        })
                      }
                      disabled={disabled}
                      size="small"
                      placeholder="Rate"
                    />
                    <Select
                      value={tier.rateType}
                      onChange={(e) =>
                        updateTier(index, {
                          rateType: e.target.value as 'flat' | 'percentage',
                        })
                      }
                      disabled={disabled}
                      size="small"
                      MenuProps={{
                        slotProps: {
                          backdrop: { invisible: true },
                        },
                      }}
                    >
                      <MenuItem value="flat">Flat</MenuItem>
                      <MenuItem value="percentage">%</MenuItem>
                    </Select>
                    <IconButton
                      onClick={() => removeTier(index)}
                      disabled={disabled}
                      size="small"
                      sx={{ color: 'error.main' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TierRow>
                ))}
              </Box>
            )}

            {(rule.tiers || []).length === 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>
                No tiers configured. Click + to add a tier.
              </Typography>
            )}
          </Box>
        );

      case 'FORMULA':
        return (
          <TextField
            label="Formula"
            value={rule.formula || ''}
            onChange={(e) => updateRule({ formula: e.target.value })}
            disabled={disabled}
            fullWidth
            size="small"
            multiline
            rows={2}
            placeholder="e.g., base * quantity * 0.1"
            helperText="Use field names as variables in your formula"
          />
        );

      case 'LOOKUP':
        return (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Lookup Table ID"
              value={rule.lookupTableId || ''}
              onChange={(e) => updateRule({ lookupTableId: e.target.value })}
              disabled={disabled}
              fullWidth
              size="small"
            />
            <TextField
              label="Key Field"
              value={rule.lookupKeyField || ''}
              onChange={(e) => updateRule({ lookupKeyField: e.target.value })}
              disabled={disabled}
              fullWidth
              size="small"
            />
          </Box>
        );

      case 'MATRIX':
        return (
          <TextField
            label="Matrix ID"
            value={rule.matrixId || ''}
            onChange={(e) => updateRule({ matrixId: e.target.value })}
            disabled={disabled}
            fullWidth
            size="small"
          />
        );

      default:
        return null;
    }
  };

  // Compact variant
  if (variant === 'compact') {
    return (
      <Box className={className} {...boxProps}>
        <StyledContainer
          $isDark={isDark}
          $neonAccent={neonAccent}
          initial={motionPresets.fadeInUp.initial}
          animate={motionPresets.fadeInUp.animate}
          transition={motionPresets.fadeInUp.transition}
        >
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Rule Name"
              value={rule.name}
              onChange={(e) => updateRule({ name: e.target.value })}
              disabled={disabled}
              size="small"
              sx={{ flex: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={rule.calculationType}
                label="Type"
                onChange={(e) =>
                  updateRule({
                    calculationType: e.target.value as CalculationType,
                  })
                }
                disabled={disabled}
              >
                {CALCULATION_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {renderCalculationFields()}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 2,
            }}
          >
            <FormControlLabel
              control={
                <GlassSwitch
                  checked={rule.isActive}
                  onChange={(_e, checked) => updateRule({ isActive: checked })}
                  disabled={disabled}
                  size="small"
                />
              }
              label={<Typography variant="body2">Active</Typography>}
            />
            <TextField
              label="Priority"
              type="number"
              value={rule.priority}
              onChange={(e) => updateRule({ priority: parseInt(e.target.value) || 1 })}
              disabled={disabled}
              size="small"
              sx={{ width: 100 }}
            />
          </Box>
        </StyledContainer>
      </Box>
    );
  }

  // Full variant (default)
  return (
    <Box className={className} {...boxProps}>
      <StyledContainer
        $isDark={isDark}
        $neonAccent={neonAccent}
        initial={motionPresets.fadeInUp.initial}
        animate={motionPresets.fadeInUp.animate}
        transition={motionPresets.fadeInUp.transition}
      >
        {/* Basic Information */}
        <GlassCard variant="flat" sx={{ mb: 3, p: 2 }} neonAccent={neonAccent}>
          <SectionHeader>Basic Information</SectionHeader>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Rule Name"
              value={rule.name}
              onChange={(e) => updateRule({ name: e.target.value })}
              disabled={disabled}
              size="small"
              sx={{ flex: 1, minWidth: 200 }}
              required
            />
            <TextField
              label="Description"
              value={rule.description || ''}
              onChange={(e) => updateRule({ description: e.target.value })}
              disabled={disabled}
              size="small"
              sx={{ flex: 2, minWidth: 300 }}
              multiline
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <GlassSwitch
                  checked={rule.isActive}
                  onChange={(_e, checked) => updateRule({ isActive: checked })}
                  disabled={disabled}
                />
              }
              label="Active"
            />
            <TextField
              label="Priority"
              type="number"
              value={rule.priority}
              onChange={(e) => updateRule({ priority: parseInt(e.target.value) || 1 })}
              disabled={disabled}
              size="small"
              sx={{ width: 100 }}
              helperText="Lower = higher priority"
            />
            <TextField
              label="Currency"
              value={rule.currency}
              onChange={(e) => updateRule({ currency: e.target.value.toUpperCase() })}
              disabled={disabled}
              size="small"
              sx={{ width: 100 }}
            />
          </Box>
        </GlassCard>

        {/* Calculation Type */}
        <GlassCard variant="flat" sx={{ mb: 3, p: 2 }} neonAccent={neonAccent}>
          <SectionHeader>Calculation Type</SectionHeader>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: 1.5,
              mb: 3,
            }}
          >
            {CALCULATION_TYPES.map((type) => (
              <CalculationTypeCard
                key={type.value}
                $isDark={isDark}
                $isSelected={rule.calculationType === type.value}
                $neonAccent={neonAccent}
                onClick={() => !disabled && updateRule({ calculationType: type.value })}
                whileHover={{ scale: disabled ? 1 : 1.02 }}
                whileTap={{ scale: disabled ? 1 : 0.98 }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: rule.calculationType === type.value ? neonStyles?.base || 'primary.main' : 'text.primary',
                  }}
                >
                  {type.label}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {type.description}
                </Typography>
              </CalculationTypeCard>
            ))}
          </Box>

          <Divider sx={{ my: 2 }} />

          {renderCalculationFields()}
        </GlassCard>

        {/* Amount Limits & Rounding */}
        <GlassCard variant="flat" sx={{ mb: 3, p: 2 }} neonAccent={neonAccent}>
          <SectionHeader>Limits & Rounding</SectionHeader>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Minimum Amount"
              type="number"
              value={rule.minAmount ?? ''}
              onChange={(e) =>
                updateRule({
                  minAmount: e.target.value === '' ? undefined : parseFloat(e.target.value),
                })
              }
              disabled={disabled}
              size="small"
              sx={{ width: 150 }}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                },
              }}
            />
            <TextField
              label="Maximum Amount"
              type="number"
              value={rule.maxAmount ?? ''}
              onChange={(e) =>
                updateRule({
                  maxAmount: e.target.value === '' ? undefined : parseFloat(e.target.value),
                })
              }
              disabled={disabled}
              size="small"
              sx={{ width: 150 }}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                },
              }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Rounding</InputLabel>
              <Select
                value={rule.roundingMode || 'none'}
                label="Rounding"
                onChange={(e) =>
                  updateRule({
                    roundingMode: e.target.value as PricingRule['roundingMode'],
                  })
                }
                disabled={disabled}
              >
                {ROUNDING_MODES.map((mode) => (
                  <MenuItem key={mode.value} value={mode.value}>
                    {mode.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {rule.roundingMode && rule.roundingMode !== 'none' && (
              <TextField
                label="Decimal Places"
                type="number"
                value={rule.roundingPrecision ?? 2}
                onChange={(e) =>
                  updateRule({
                    roundingPrecision: parseInt(e.target.value) || 2,
                  })
                }
                disabled={disabled}
                size="small"
                sx={{ width: 120 }}
              />
            )}
          </Box>
        </GlassCard>

        {/* Conditions */}
        <GlassCard variant="flat" sx={{ p: 2 }} neonAccent={neonAccent}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <SectionHeader sx={{ mb: 0 }}>Conditions</SectionHeader>
            <IconButton
              onClick={addCondition}
              disabled={disabled}
              size="small"
              sx={{
                color: neonStyles?.base || 'primary.main',
                '&:hover': {
                  background: alpha(neonStyles?.base || theme.palette.primary.main, 0.1),
                },
              }}
            >
              <AddIcon />
            </IconButton>
          </Box>

          {(rule.conditions || []).length === 0 && (
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>
              No conditions. Rule will always apply. Click + to add a condition.
            </Typography>
          )}

          {(rule.conditions || []).map((condition, index) => (
            <Box key={condition.id}>
              <ConditionRow
                $isDark={isDark}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
              >
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Field</InputLabel>
                  <Select
                    value={condition.field}
                    label="Field"
                    onChange={(e) => updateCondition(index, { field: e.target.value })}
                    disabled={disabled}
                  >
                    {availableFields.map((field) => (
                      <MenuItem key={field.name} value={field.name}>
                        {field.label}
                      </MenuItem>
                    ))}
                    {availableFields.length === 0 && (
                      <MenuItem value="">
                        <em>No fields available</em>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Operator</InputLabel>
                  <Select
                    value={condition.operator}
                    label="Operator"
                    onChange={(e) =>
                      updateCondition(index, {
                        operator: e.target.value as ConditionOperator,
                      })
                    }
                    disabled={disabled}
                  >
                    {CONDITION_OPERATORS.map((op) => (
                      <MenuItem key={op.value} value={op.value}>
                        {op.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Value"
                  value={condition.value as string}
                  onChange={(e) => updateCondition(index, { value: e.target.value })}
                  disabled={disabled}
                  size="small"
                  sx={{ flex: 1, minWidth: 150 }}
                  placeholder={
                    CONDITION_OPERATORS.find((op) => op.value === condition.operator)?.requiresArray
                      ? 'value1, value2, ...'
                      : 'Enter value'
                  }
                />

                <IconButton
                  onClick={() => removeCondition(index)}
                  disabled={disabled}
                  size="small"
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ConditionRow>

              {index < (rule.conditions || []).length - 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                  <ConnectorChip
                    label={condition.connector || 'AND'}
                    onClick={() => !disabled && toggleConnector(index)}
                    $isDark={isDark}
                    $neonAccent={neonAccent}
                    size="small"
                  />
                </Box>
              )}
            </Box>
          ))}
        </GlassCard>
      </StyledContainer>
    </Box>
  );
};

RuleBuilder.displayName = 'RuleBuilder';

export default RuleBuilder;
